import { apiClient } from '../lib/api';
import { ApiResponseSchema } from '../schemas';
import { provinceBoundaryApi } from './provinceBoundaryApi';
import {
	ApplyResultSchema,
	ConversionBatchDetailSchema,
	ConversionBatchListSchema,
	ConversionBatchSchema,
	ConversionProgressSchema,
	CreateConversionInputSchema,
	PreviewResultSchema,
	RevertResultSchema,
	UploadMappingsResultSchema,
	type ApplyResult,
	type BoundaryFeatureCollection,
	type ConversionBatch,
	type ConversionBatchDetail,
	type ConversionProgress,
	type CreateConversionInput,
	type PreviewResult,
	type RevertResult,
	type UploadMappingsResult,
} from '../schemas/provinceConversion';

interface ConversionSseErrorBody {
	message?: string;
	code?: string;
}

/**
 * Đọc stream SSE của một `POST` (khác `EventSource` — chỉ hỗ trợ `GET`) cho
 * apply/revert đợt chuyển đổi. Cùng khuôn xử lý với `import/execute` (backend
 * `ImportController.executeImport`), chỉ khác cách gọi vì đây là `POST`.
 */
async function streamConversionPost<T>(
	url: string,
	resultSchema: { parse: (data: unknown) => T },
	onProgress?: (progress: ConversionProgress) => void
): Promise<T> {
	const response = await fetch(url, { method: 'POST', credentials: 'include' });

	// `response.body` là ReadableStream kể cả với 429/401/409/500, nên chỉ kiểm
	// `body` sẽ để lỗi HTTP trôi vào vòng đọc stream rồi báo "không rõ thành công
	// hay chưa" — đúng lúc backend đã trả lỗi rõ ràng.
	if (!response.ok || !response.body) {
		let body: ConversionSseErrorBody | null = null;
		try {
			body = await response.json();
		} catch {
			// Thân lỗi không phải JSON — giữ nguyên `body = null`.
		}
		throw new Error(body?.message ?? `Yêu cầu thất bại (mã ${response.status}).`);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let finalResult: T | null = null;

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });

		const frames = buffer.split('\n\n');
		buffer = frames.pop() ?? '';
		for (const frame of frames) {
			const dataLine = frame.split('\n').find((line) => line.startsWith('data: '));
			if (!dataLine) continue;
			const raw = dataLine.slice('data: '.length);
			if (raw === 'done') continue;

			const data = JSON.parse(raw) as Record<string, unknown>;
			if (data.status === 'error') {
				throw new Error(typeof data.message === 'string' ? data.message : 'Lỗi không xác định.');
			}
			if (data.status === 'completed') {
				finalResult = resultSchema.parse(data);
			} else {
				onProgress?.(ConversionProgressSchema.parse(data));
			}
		}
	}

	if (!finalResult) {
		throw new Error('Kết nối bị ngắt trước khi hoàn tất — không rõ thao tác đã thành công hay chưa.');
	}
	return finalResult;
}

/**
 * Client cho vòng đời đợt chuyển đổi mã tỉnh (Phase 3 admin API) và cho nền
 * bản đồ hiện hành dùng trong `ConversionPreviewMap` (Phase 4 public API).
 *
 * `getCurrentBoundaries` DELEGATE sang `provinceBoundaryApi.getCurrent()`
 * (M4: trước review gọi thẳng `/provinces/boundaries` với schema tự định
 * nghĩa riêng, trùng gần như y hệt `provinceBoundaryApi.ts` — dựng lúc
 * Phase 5/6 chạy song song để tránh đụng file. Cả hai phase đã xong nên
 * không còn lý do giữ hai lời gọi HTTP + hai schema cho CÙNG một endpoint).
 */
export const provinceConversionApi = {
	/**
	 * Tải file `.xlsx` mẫu cho upload ánh xạ (phase 2). Dùng lại
	 * `apiClient.downloadBlob` (đã có sẵn cho export) thay vì tự dựng lại
	 * cùng logic blob/link/revoke — filename lấy từ `Content-Disposition` do
	 * `ProvinceConversionController.downloadTemplate` đặt.
	 */
	downloadTemplate: async (): Promise<void> => {
		await apiClient.downloadBlob('/admin/province-conversions/template', undefined, 'mau-anh-xa-tinh.xlsx');
	},

	list: async (): Promise<ConversionBatch[]> => {
		const response = await apiClient.get('/admin/province-conversions');
		return ApiResponseSchema(ConversionBatchListSchema).parse(response.data).data;
	},

	create: async (input: CreateConversionInput): Promise<ConversionBatch> => {
		const payload = CreateConversionInputSchema.parse(input);
		const response = await apiClient.post('/admin/province-conversions', payload);
		return ApiResponseSchema(ConversionBatchSchema).parse(response.data).data;
	},

	uploadMappings: async (dotId: string, file: File): Promise<UploadMappingsResult> => {
		const formData = new FormData();
		formData.append('file', file);
		const response = await apiClient.raw.post(`/admin/province-conversions/${dotId}/mappings`, formData);
		return ApiResponseSchema(UploadMappingsResultSchema).parse(response.data).data;
	},

	getMappings: async (dotId: string): Promise<ConversionBatchDetail> => {
		const response = await apiClient.get(`/admin/province-conversions/${dotId}/mappings`);
		return ApiResponseSchema(ConversionBatchDetailSchema).parse(response.data).data;
	},

	preview: async (dotId: string): Promise<PreviewResult> => {
		const response = await apiClient.post(`/admin/province-conversions/${dotId}/preview`);
		return ApiResponseSchema(PreviewResultSchema).parse(response.data).data;
	},

	/** Áp dụng đợt — server báo tiến độ qua SSE (đọc chuỗi -> gộp ranh giới n/tổng -> ghi -> xong). */
	apply: async (dotId: string, onProgress?: (progress: ConversionProgress) => void): Promise<ApplyResult> => {
		return streamConversionPost(`/api/admin/province-conversions/${dotId}/apply`, ApplyResultSchema, onProgress);
	},

	/** Hoàn tác đợt APPLIED mới nhất — cùng cơ chế tiến độ SSE với `apply`. */
	revert: async (dotId: string, onProgress?: (progress: ConversionProgress) => void): Promise<RevertResult> => {
		return streamConversionPost(`/api/admin/province-conversions/${dotId}/revert`, RevertResultSchema, onProgress);
	},

	remove: async (dotId: string): Promise<void> => {
		await apiClient.delete(`/admin/province-conversions/${dotId}`);
	},

	/** Nền cho `ConversionPreviewMap` — FeatureCollection trần (Phase 4), không bọc envelope. */
	getCurrentBoundaries: async (): Promise<BoundaryFeatureCollection> => {
		return (await provinceBoundaryApi.getCurrent()) as BoundaryFeatureCollection;
	},
};
